<template lang="pug">
form.login-form(name='form', novalidate)

	.login-body

		h3 New Customer
		// p: b New Customer
		br

		p Creating a new account is free, fast and simple. All you need to provide is a user name, a valid email, and a password. This is required in order to save any information that you enter for your project. By registering, you implicitly agree to our #[a(:href='navLinks.termsAndConditions' target='_blank') Terms and Conditions.]
		br

		.alert.alert-danger(v-if='errors.length')
			b Please correct the following error(s):
			ul
				li(v-for='error in errors') {{ error }}

		.login-lbl
			label First Name
			b-form-input.login-field(type='text', name='fname', v-model='user.contact.firstName', required, autofocus, novalidate='', @blur='refreshValidation')

		.login-lbl
			label Last Name
			b-form-input.login-field(type='text', name='lname', v-model='user.contact.lastName', required, autofocus, novalidate='', @blur='refreshValidation')

		.login-lbl
			// label {{ $t('label.username') }}
			label User Name:
			b-form-input.login-field(type='text', name='login', v-model='user.account.username', required, autofocus, novalidate='', @blur='refreshValidation')

		.login-lbl
			label Email Address:
			b-form-input.login-field(type='email', name='email', v-model='user.contact.email', required, autofocus, novalidate='', @blur='refreshValidation')

		.login-lbl
			// label {{ $t('label.password') }}
			Label Password:
			b-form-input.login-field(name="password", type='password', v-model='user.account.password', required, novalidate='', @blur='refreshValidation')

		.login-lbl
			// label {{ $t('label.retypePass') }}
			Label Confirm Password:
			b-form-input.login-field(name="confirmPassword", type='password', v-model='user.account.retypePassword', required, novalidate='', @blur='refreshValidation')

		.btn-login-cnt
			b-button(:variant="(hasSubmitted && errors.length) ? 'secondary' : 'success'", :disabled='hasSubmitted && errors.length > 0', size="lg", @click='register') Register
</template>

<script>
import Vue from 'vue'
import { mapActions } from 'vuex'
import store from 'CommonApp/store'

export default {
	name: 'new-customer-form',
	data () {
		return {
			// Set true by the first Register click. Before that, the form is
			// left alone entirely — no error list, no button greying — so
			// typing doesn't trigger anything. After it's true, refreshValidation
			// (wired to each field's @blur) keeps errors/the button current as
			// the user corrects fields one at a time.
			hasSubmitted: false,
			errors: [],
			navLinks: {},
			user: {
				account: {
					username: null,
					password: null,
					retypePassword: null
				},
				contact: {
					firstName: null,
					lastName: null,
					email: null
				}
			}
		}
	},
	beforeMount () {
		this.navLinks = this.$e24Common.storageService.findItem('navLinks', true) || {}
	},
	methods: {
		...mapActions(['infoDialog']),

		loginAttempt (username, password) {
			let authError = null

			// Try to login
			Vue.e24.security.login(username, password).then((loggedIn) => {
				if (!loggedIn) {
					// If we get here then the login failed due to bad credentials
					authError = 'login.error.invalidCredentials'
				} else {
					// let redirect = (this.entryUrl || {name: 'project.list'})
					let redirect = (this.entryUrl || {name: 'project.new'})
					// Clean requested url
					store.dispatch({
						type: 'updateEntryUrl',
						value: null
					})
					// Login success
					this.$router.push(redirect)
				}
			}, (err) => {
				// If we get here then there was a problem with the login request to the server
				console.error('Server is unavailable or returned an error', err)
				authError = 'login.error.serverError'
			})

			// Goto login and show error
			if (authError) {
				this.$router.push({name: 'login', params: { goodbye: '', redirect: '', error: authError }})
			}
		},
		// Pure validation: returns the list of current error messages without
		// touching component state. Called explicitly from register() and
		// refreshValidation() below — deliberately not reactive/computed, so
		// nothing re-checks on every keystroke.
		validate () {
			const errors = []

			if (!this.user.account.username) {
				errors.push('User Name required.')
			}

			const passwordRequired = 'Password required. Must be between 12 and 32 characters, and include at least one letter, one number and one symbol.'
			// Strength and confirmation are checked independently so that a
			// weak password that also lacks (or fails) its confirmation
			// reports both problems at once, rather than one per attempt.
			const { password, retypePassword } = this.user.account
			if (!password) {
				errors.push(passwordRequired)
			} else {
				try {
					// No confirmation argument: the service then checks strength only
					Vue.jnx.validationService.password(password)
				} catch (err) {
					if (err.message === 'strength') {
						errors.push(passwordRequired)
					}
				}
				if (!retypePassword) {
					errors.push('Please confirm your password.')
				} else if (password !== retypePassword) {
					errors.push('The passwords you entered do not match.')
				}
			}

			if (!this.user.contact.email) {
				errors.push('Email required.')
			} else if (!Vue.jnx.validationService.email(this.user.contact.email)) {
				errors.push('Valid email required.')
			}
			if (!this.user.contact.firstName) {
				errors.push('First Name required.')
			}
			if (!this.user.contact.lastName) {
				errors.push('Last Name required.')
			}

			return errors
		},

		// Re-checks validation after hasSubmitted goes true, wired to each
		// field's @blur. Deliberately not on every keystroke — only when the
		// user leaves a field, so correcting one thing doesn't repaint the
		// whole error list and button state mid-keystroke.
		refreshValidation () {
			if (this.hasSubmitted) {
				this.errors = this.validate()
			}
		},

		// Create new account.
		register () {
			this.hasSubmitted = true
			this.errors = this.validate()

			if (this.errors.length === 0) {
				Vue.jnx.userActionService.insert(this.user).then((result) => {
					console.log('Inserted user', result)
					// Login
					this.loginAttempt(this.user.account.username, this.user.account.password)
				}, (error) => {
					console.log('Error creating account', error)

					// The server tells us which field conflicted via `attribute`
					// (janux-persist: "account" for a duplicate username, from
					// UserService.insert; "contact.emails" for a duplicate email,
					// from PartyDao's pre-insert duplicate check) — clear exactly
					// that field so the user isn't left staring at a rejected
					// value with no indication of what to change.
					let message
					if (error.attribute === 'account') {
						message = 'The User Name that you provided already exists. Please provide a different User Name'
						this.user.account.username = null
					} else if (error.attribute === 'contact.emails') {
						message = 'The email address that you provided has already been registered. Please provide a different Email Address'
						this.user.contact.email = null
					} else {
						message = error.message || 'Something went wrong creating your account.'
					}

					// The dialog states the specific problem once. The persistent
					// red box underneath it should not go on repeating that after
					// it's dismissed — the field is empty now, so the ordinary
					// "required" validation already says the right thing.
					this.errors = this.validate()
					this.infoDialog(message)
				})
			}
		}
	},
	computed: {
		entryUrl () {
			return this.$store.getters.entryUrl
		}
	}
}
</script>



// WEBPACK FOOTER //
// src/app/login/new-customer.vue